import React, {useEffect, useMemo, useRef} from 'react';
import echarts from '@/utils/echartsCore';
import {DataLabelPosition} from 'entities/chart';
import {
    buildLineDataLabelConfig,
    getLinePointSymbolConfig,
} from '@/views/storyboard/utils/chartDataLabels';
import {buildChartImageDownloadToolboxFeatures} from '@/views/storyboard/utils/chartImageDownload';
import i18n from 'i18n';

export interface LineChartProps {
    data: any;
    movingAverage: number | null | undefined;
    linearRegression?: boolean;
    chartQueryOutput?: string;
    comparablePeriod?: boolean;
    distinctKeysArray: string[];
    maxValue?: number;
    height?: number;
    width?: number;
    onLegendToggle?: (hiddenKeys: string[]) => void;
    chartSettingsView?: boolean;
    title?: string;
    description?: string;
    dataLabelPosition?: DataLabelPosition;
    datePeriod?: string;
}

const LineCharts: React.FC<LineChartProps> = (
    {
        data,
        movingAverage,
        linearRegression,
        chartQueryOutput,
        comparablePeriod,
        distinctKeysArray,
        maxValue = 0,
        height = 1,
        width = 1,
        onLegendToggle,
        chartSettingsView,
        title = '',
        description = '',
        dataLabelPosition = DataLabelPosition.Off,
        datePeriod = '',
    }
) => {

    const chartRef = useRef(null);

    const showToolbox = !chartSettingsView && height !== 1;

    const yAxisMax = distinctKeysArray.length > 1
        ? Math.ceil(maxValue) + Math.floor(maxValue * 0.1)
        : undefined;

    const {chartHeight, gridBottom} = useMemo(() => {
        const chartHeight = '100%';
        let gridBottom = '30%';

        if (height === 2 || height === 3) {
            gridBottom = '20%';
        } else if (height === 4 || height === 5) {
            gridBottom = chartSettingsView ? '20%' : '15%';
        }

        return {chartHeight, gridBottom};
    }, [height, chartSettingsView]);

    const comparableCountData = useMemo(() => Array.isArray(data) ? data.map((item: any) => item.comparableDocCount) : [], [data]);
    const movingAverageData = useMemo(() => Array.isArray(data) ? data.map((item: any) => item.movingAvg) : [], [data]);
    const linearRegressionData = useMemo(() => Array.isArray(data) ? data.map((item: any) => item.linearReg) : [], [data]);

    const baseLegend = useMemo(() =>
        Array.isArray(distinctKeysArray) ? distinctKeysArray.map(key => key === 'docCount' ? i18n.t('ch_count.text') : key) : [], [distinctKeysArray]
    );

    const extraLegendKeys = useMemo(() => {
        const extras = [];
        if (chartQueryOutput === 'timeline_by_date' && comparablePeriod) {
            extras.push(i18n.t('ch_comparable_count.text'));
        }
        if (typeof movingAverage === 'number') {
            extras.push(`${i18n.t('ch_moving_average.text')} (${movingAverage})`);
        }
        if (linearRegression) {
            extras.push(i18n.t('ch_trendline.text'));
        }
        return extras;
    }, [chartQueryOutput, comparablePeriod, movingAverage, linearRegression]);

    const legendData = useMemo(() => [...baseLegend, ...extraLegendKeys], [baseLegend, extraLegendKeys]);

    const lineLabelConfig = useMemo(
        () => buildLineDataLabelConfig(dataLabelPosition),
        [dataLabelPosition],
    );

    const linePointSymbol = useMemo(
        () => getLinePointSymbolConfig(dataLabelPosition),
        [dataLabelPosition],
    );

    useEffect(() => {
        if (!chartRef.current) {return;}

        const linechart = echarts.init(chartRef.current);
        const handleResize = () => {
            if (linechart && !linechart.isDisposed()) {
                linechart.resize();
            }
        };

        const handleChatSidebarChange = () => {
            // Add a small delay to allow CSS transitions to complete
            setTimeout(handleResize, 350);
        };

        // Observe container size changes
        let resizeObserver: any = null;
        if (typeof window !== 'undefined' && 'ResizeObserver' in window && chartRef.current) {
            resizeObserver = new (window as any).ResizeObserver(() => {
                handleResize();
            });
            resizeObserver.observe(chartRef.current);
        }

        window.addEventListener('resize', handleResize);
        window.addEventListener('chatSidebarOpened', handleChatSidebarChange);
        window.addEventListener('chatSidebarClosed', handleChatSidebarChange);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('chatSidebarOpened', handleChatSidebarChange);
            window.removeEventListener('chatSidebarClosed', handleChatSidebarChange);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            if (linechart && !linechart.isDisposed()) {
                linechart.dispose();
            }
        }
    }, []);

    // Ensure the chart is resized when the container height changes
    useEffect(() => {
        if (!chartRef.current) {return;}

        const chartInstance = echarts.getInstanceByDom(chartRef.current);
        if (!chartInstance || chartInstance.isDisposed()) {return;}

        chartInstance.resize();
    }, [height]);

    useEffect(() => {
        if (!chartRef.current) {return;}

        const chartInstance = echarts.getInstanceByDom(chartRef.current);
        if (!chartInstance || chartInstance.isDisposed()) {return;}

        const option = {
            textStyle: {
                fontFamily: '"Rubik", sans-serif',
                fontSize: 13,
                fontWeight: 100,
            },
            tooltip: {
                trigger: 'axis',
                confine: true,
                axisPointer: {
                    type: 'cross',
                    label: {
                        backgroundColor: '#6a7985'
                    }
                },
                textStyle: {
                    fontWeight: 400,
                },
                formatter: function (params: any) {
                    let result = params[0].axisValueLabel + '<br/><table>';

                    (params as Array<any>).forEach((item) => {
                        if (item.data != null && item.data !== '' && !isNaN(item.data)) {
                            result += `
                                <tr>
                                    <td style="text-align: left;">${item.marker}${item.seriesName}</td>
                                    <td style="text-align: right; padding-left: 30px;">${item.data}</td>
                                </tr>
                            `;
                        }
                    });

                    result += '</table>';
                    return result;
                },
            },
            toolbox: {
                show: showToolbox,
                orient: 'vertical',
                itemSize: 12,
                itemGap: 8,
                feature: {
                    dataZoom: {
                        yAxisIndex: 'none'
                    },
                    dataView: {
                        optionToContent: function (opt: any) {
                            const axisData = opt.xAxis[0].data;
                            const series = opt.series;
                            const headerColumns = opt.series.map((x: {name: {toString: () => string;};}) => '<td>' + x.name.toString() + '</td>').join('');
                            let table =
                                '<table class="table table-hover g-table w-100" style="font-size:0.8em">' +
                                '<thead><tr>' +
                                '<td>' + i18n.t('ch_datapoint.text') + '</td>' +
                                headerColumns +
                                '</tr><thead><tbody>';
                            for (let i = 0, l = axisData.length - 1; i < l; i++) {
                                table += '<tr><td>' + axisData[i] + '</td>'
                                for (let j = 0; j < series.length; j++) {
                                    if (series[j].data[i]) {
                                        table += '<td>' + series[j].data[i] + '</td>'
                                    }
                                    else {
                                        table += '<td></td>'
                                    }
                                }
                                table += '</tr>';
                            }
                            table += '</tbody></table>';
                            return table;
                        },
                        readOnly: true,
                    },
                    ...buildChartImageDownloadToolboxFeatures({
                        title,
                        description,
                        datePeriod,
                        getChartInstance: () => (
                            chartRef.current ? echarts.getInstanceByDom(chartRef.current) : null
                        ),
                    }, showToolbox),
                }
            },
            legend: {
                type: 'scroll',
                orient: 'horizontal',
                data: legendData,
                selectedMode: 'multiple',
                bottom: '8%',
            },
            grid: {
                top: '6%',
                left: '4%',
                right: '6%',
                bottom: gridBottom,
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: data.map((item: {labelShort: any;}) => item.labelShort),
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: yAxisMax,
            },
            series: [
                ...distinctKeysArray.map((dataKey: any) => ({
                    type: 'line',
                    encode: {x: 'labelShort', y: dataKey},
                    name: dataKey === 'docCount' ? i18n.t('ch_count.text') : dataKey,
                    data: data.map((item: any) => item[dataKey]),
                    ...linePointSymbol,
                    connectNulls: true,
                    smooth: true,
                    ...(lineLabelConfig ? {label: lineLabelConfig} : {}),
                })),

                chartQueryOutput === 'timeline_by_date' && comparablePeriod ? {
                    type: 'line',
                    encode: {x: 'labelShort', y: 'comparableDocCount'},
                    name: i18n.t('ch_comparable_count.text'),
                    data: comparableCountData,
                    lineStyle: {
                        type: 'dashed',
                    },
                    ...linePointSymbol,
                    connectNulls: true,
                    ...(lineLabelConfig ? {label: lineLabelConfig} : {}),
                } : null,

                movingAverage != null ? {
                    type: 'line',
                    encode: {x: 'labelShort', y: 'movingAvg'},
                    name: i18n.t('ch_moving_average_label.text', {movingAverageDays: movingAverage}),
                    data: movingAverageData,
                    ...linePointSymbol,
                    ...(lineLabelConfig ? {label: lineLabelConfig} : {}),
                } : null,

                linearRegression ? {
                    type: 'line',
                    encode: {x: 'labelShort', y: 'linearReg'},
                    name: i18n.t('ch_trendline.text'),
                    data: linearRegressionData,
                    ...linePointSymbol,
                    connectNulls: true,
                    ...(lineLabelConfig ? {label: lineLabelConfig} : {}),
                } : null,
            ].filter(Boolean)
        };

        chartInstance.setOption(option, {replaceMerge: ['series']});

        chartInstance.on('legendselectchanged', function (params: any) {
            const hiddenSeries = [];

            for (let [seriesName, isSelected] of Object.entries(params.selected)) {

                if (seriesName === i18n.t('ch_count.text')) {
                    seriesName = 'docCount';
                }

                if (!isSelected) {
                    hiddenSeries.push(seriesName);
                }
            }

            onLegendToggle?.(hiddenSeries);
        });

    }, [data, movingAverage, distinctKeysArray, comparablePeriod, chartQueryOutput, linearRegression, maxValue, height, dataLabelPosition, lineLabelConfig, linePointSymbol, title, description, datePeriod, showToolbox]);

    return <div ref={chartRef} style={{width: '100%', height: chartHeight}} />;

}

export default LineCharts;